import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, interval, switchMap, takeWhile, tap, BehaviorSubject, timer, race, throwError, firstValueFrom, filter } from 'rxjs';
import { environment } from '../../environments/environment';
import { CompaniesService } from './companies.service';

export interface JobStatus {
  jobId: string;
  type: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress: number;
  message: string;
  results?: {
    platform: string;
    campaignId: string;
    leadsProcessed: number;
    leadsSkipped: number;
    errors: string[];
    platformJobId: string;
  };
  error?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: any;
}

export interface JobResponse {
  success: boolean;
  data?: JobStatus;
  error?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class JobService {
  private _baseUrl = environment.baseUrl;
  private activePolls = new Map<string, BehaviorSubject<JobStatus | null>>();

  constructor(
    private http: HttpClient,
    private companiesService: CompaniesService
  ) {}

  /**
   * Get current job status
   */
  getJobStatus(jobId: string): Observable<JobResponse> {
    const currentCompany = this.companiesService.getSelectedCompany();
    const companyId = currentCompany?.companyId;

    if (!companyId) {
      console.warn('No company selected, fetching job status without companyId');
      return this.http.get<JobResponse>(`${this._baseUrl}/jobs/${jobId}/status`);
    }

    return this.http.get<JobResponse>(`${this._baseUrl}/jobs/${jobId}/status?companyId=${companyId}`);
  }

  /**
   * Poll job status with automatic updates
   * Returns an Observable that emits job status updates until completion
   */
  pollJobStatus(jobId: string, pollInterval = 5000, maxDuration = 300000): Observable<JobStatus> {
    // Check if we already have an active poll for this job
    if (this.activePolls.has(jobId)) {
      return this.activePolls.get(jobId)!.asObservable().pipe(
        takeWhile(job => job !== null)
      ) as Observable<JobStatus>;
    }

    // Create new polling subject
    const jobSubject = new BehaviorSubject<JobStatus | null>(null);
    this.activePolls.set(jobId, jobSubject);

    // Create polling observable with timeout
    const polling$ = interval(pollInterval).pipe(
      switchMap(() => this.getJobStatus(jobId)),
      tap(response => {
        console.log('Job service received response:', response);
        if (response.success && response.data) {
          const jobStatus = response.data;
          console.log('Job service emitting job status:', jobStatus);
          jobSubject.next(jobStatus);

          // Stop polling if job is complete
          if (jobStatus.status === 'completed' || jobStatus.status === 'failed') {
            this.stopPolling(jobId);
          }
        } else {
          // Handle API error response
          console.error('Job status API returned error:', response);
          const errorJob: JobStatus = {
            jobId: jobId,
            type: 'lead_distribution',
            status: 'failed',
            progress: 0,
            message: 'Failed to fetch job status',
            error: response.error || 'Unknown error occurred',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          jobSubject.next(errorJob);
          this.stopPolling(jobId);
        }
      }),
      takeWhile((response: JobResponse) => {
        const job = response.data;
        if (!job) return false;
        
        console.log(`Job ${jobId} status: ${job.status}, progress: ${job.progress}%`);
        
        // Continue polling while job is not in a final state
        const shouldContinue = job.status === 'queued' || job.status === 'processing';
        return shouldContinue;
      }, true) // Include the final emission
    );
    
    // Create timeout observable
    const timeout$ = timer(maxDuration).pipe(
      switchMap(() => {
        console.warn(`Job ${jobId} polling timed out after ${maxDuration}ms`);
        const timeoutJob: JobStatus = {
          jobId: jobId,
          type: 'lead_distribution',
          status: 'failed',
          progress: 0,
          message: 'Job polling timed out',
          error: 'The job took too long to complete and polling was stopped',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        jobSubject.next(timeoutJob);
        this.stopPolling(jobId);
        return throwError(() => new Error('Job polling timeout'));
      })
    );
    
    // Race polling against timeout
    race(polling$, timeout$).subscribe({
      error: (error) => {
        // Don't log timeout errors as they're expected
        if (error.message !== 'Job polling timeout') {
          console.error(`Error polling job ${jobId}:`, error);
          
          // Create a failed job status to notify the component
          const errorJob: JobStatus = {
            jobId: jobId,
            type: 'lead_distribution',
            status: 'failed',
            progress: 0,
            message: 'Connection error while checking job status',
            error: error.message || 'Network error occurred',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          jobSubject.next(errorJob);
          this.stopPolling(jobId);
        }
      }
    });

    // Return the observable that filters out null values
    // Skip the initial null value and only emit actual job statuses
    return jobSubject.asObservable().pipe(
      filter((job): job is JobStatus => job !== null),
      takeWhile(job => job!.status !== 'completed' && job!.status !== 'failed', true)
    ) as Observable<JobStatus>;
  }

  /**
   * Stop polling for a specific job
   */
  stopPolling(jobId: string): void {
    const subject = this.activePolls.get(jobId);
    if (subject) {
      subject.complete();
      this.activePolls.delete(jobId);
    }
  }

  /**
   * Stop all active polling
   */
  stopAllPolling(): void {
    this.activePolls.forEach((subject, jobId) => {
      subject.complete();
    });
    this.activePolls.clear();
  }

  /**
   * Get the current status of a job without polling
   */
  async getJobStatusOnce(jobId: string): Promise<JobStatus | null> {
    try {
      const response = await firstValueFrom(this.getJobStatus(jobId));
      return response?.success ? (response.data || null) : null;
    } catch (error) {
      console.error(`Error fetching job status for ${jobId}:`, error);
      return null;
    }
  }
}